import { Injectable } from "@angular/core";
import { AnalyticsService } from "./analytics.service";
import { DebuggerService } from "./debugger.service";

/**
 * LoggerService — the single entry point for explicit logging in components and services.
 *
 * ## How error tracking works in this app
 *
 * There are two layers of automatic error capture:
 *   1. **GlobalErrorHandler** (`global-error-handler.service.ts`) — catches all uncaught
 *      Angular errors (thrown from components, effects, guards, etc.) and forwards them
 *      to AnalyticsService.trackError(). You do NOT need to call the logger for those.
 *
 *   2. **AnalyticsService.setupGlobalErrorCapture()** — catches raw `window.error` and
 *      `unhandledrejection` events (e.g. errors from third-party scripts, unhandled
 *      Promises). Also fully automatic.
 *
 * ## When to use LoggerService
 *
 * Use this service for **caught** errors and **intentional** log output in your code:
 *
 * ```ts
 * // ✅ Caught error in a try/catch — use the logger so it reaches analytics
 * try {
 *   await this.someService.doSomething();
 * } catch (e) {
 *   this.logger.errorWithContext('MyComponent', 'Failed to do something', e);
 * }
 *
 * // ✅ Debug output only visible in debug mode
 * this.logger.debug('MyComponent', 'Loaded items', items);
 *
 * // ✅ Warning (console only, does not go to analytics)
 * this.logger.warn('MyComponent', 'No project selected');
 *
 * // ❌ Don't call console.error() directly — it bypasses analytics
 * console.error('something went wrong');
 * ```
 *
 * ## Do NOT use LoggerService for
 * - Errors you intentionally re-throw (GlobalErrorHandler will catch them)
 * - Navigation errors or HTTP errors that Angular already captures
 *
 * ## Relationship to AnalyticsService
 * `error()` and `errorWithContext()` forward to `AnalyticsService.trackError()` automatically.
 * You do not need to call AnalyticsService directly for error events — use this service.
 * Use AnalyticsService directly only for non-error tracking: `trackAction`, `trackPerformance`.
 */
@Injectable({ providedIn: 'root' })
export class LoggerService {
  private debugMode = false;

  constructor(private debuggerService: DebuggerService, private analyticsService: AnalyticsService) {
    // Subscribe to global debug mode state
    this.debuggerService.debugMode$.subscribe(mode => {
      this.debugMode = mode;
    });
  }

  /**
   * Debug-level logging - only shown when debug mode is enabled
   * @param context Component or feature name for context
   * @param message Log message
   * @param args Additional data to log
   */
  debug(context: string, message: string, ...args: any[]): void {
    if (!this.debugMode) return;

    const timeString = new Date().toLocaleTimeString();
    console.log(`[${context}] ${message} (${timeString})`, ...args);
  }

  /**
   * Info-level logging - always shown
   * @param message Log message
   */
  log(message: string): void {
    const timeString: string = new Date().toLocaleTimeString();
    console.log(`${message} (${timeString})`);
  }

  /**
   * Info-level logging with context
   * @param context Component or feature name
   * @param message Log message
   * @param args Additional data to log
   */
  info(context: string, message: string, ...args: any[]): void {
    const timeString = new Date().toLocaleTimeString();
    console.log(`[${context}] ${message} (${timeString})`, ...args);
  }

  /**
   * Warning-level logging
   * @param context Component or feature name
   * @param message Warning message
   * @param args Additional data to log
   */
  warn(context: string, message: string, ...args: any[]): void {
    const timeString = new Date().toLocaleTimeString();
    console.warn(`[${context}] ${message} (${timeString})`, ...args);
  }

  /**
   * Error-level logging
   * @param message Error message
   */
  error(message: string): void {
    console.error(`ERROR: ${message}`);
    this.analyticsService.trackError(message, 'logger');
  }

  /**
   * Error-level logging with context
   * @param context Component or feature name
   * @param message Error message
   * @param args Additional data to log
   */
  errorWithContext(context: string, message: string, ...args: any[]): void {
    const timeString = new Date().toLocaleTimeString();
    console.error(`[${context}] ERROR: ${message} (${timeString})`, ...args);

    // Pass the real error object (the first extra arg, by convention) as the
    // primary error so AnalyticsService.normalizeError() can extract its type,
    // stack and build a stable dedup signature. Previously the human `message`
    // string was passed instead, which collapsed every logged error into a single
    // "errorType: string" bucket and dropped the stack entirely. The human message
    // is preserved as metadata.note; Error objects in the args are converted to
    // plain objects so they don't serialize to `{}` over the wire.
    const candidate: unknown = args.length ? args[0] : undefined;
    const looksLikeError =
      candidate instanceof Error ||
      (!!candidate && typeof candidate === 'object' &&
        'message' in (candidate as object) &&
        ('stack' in (candidate as object) ||
          'status' in (candidate as object) ||
          'name' in (candidate as object)));
    const primaryError = looksLikeError ? candidate : message;

    const metadata: Record<string, any> = { note: message };
    if (args.length) {
      metadata['args'] = args.map(arg => this.toSerializable(arg));
    }

    this.analyticsService.trackError(primaryError, context, undefined, metadata);
  }

  /**
   * Convert Error instances to plain, JSON-serializable objects. `JSON.stringify`
   * drops Error.name/message/stack because they are non-enumerable, so raw Errors
   * placed in analytics metadata would otherwise be transmitted as `{}`.
   */
  private toSerializable(value: unknown): unknown {
    if (value instanceof Error) {
      const plain: Record<string, unknown> = {
        name: value.name,
        message: value.message,
        stack: value.stack
      };
      // Preserve any own-enumerable custom fields (e.g. codes) attached to the error.
      for (const key of Object.keys(value)) {
        plain[key] = (value as any)[key];
      }
      return plain;
    }
    return value;
  }
}
